'use client';
import { ICartItem } from '@/api/types/cart';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useContext } from 'react';

// 导入子组件和hooks
import LensProcessDialog from '@/app/[variants]/lens-process/LensProcessDialog';
import CartDetailPopup from '../cartItemComponents/CartDetailPopup';
import CartItemDialog from '../cartItemComponents/CartItemDialog';
import ColorChangePopup from '../cartItemComponents/ColorChangePopup';
import LimitBuyDialog from '../cartItemComponents/LimitBuyDialog';
import RedeemedConfirmDialog from '../cartItemComponents/RedeemedConfirmDialog';
import { useCartItem } from '../hooks/useCartItem';
import { CartItemMobile } from './CartItem.m';
import { CartItemPC } from './CartItem.pc';

// 类型定义
interface CartItemProps {
  cartItemData: ICartItem;
  batchFlag: boolean;
  onReload: (cartData?: any, modifiedCartId?: number) => void;
  selected?: boolean;
  itemDisabled?: boolean; // 商品是否禁用
  onSelectChange?: (selected: boolean) => void;
  itemPromotion?: any;
  currency?: any;
  itemType?: number; // 1：普通类型，2：礼包类型, 3: 套装（已不用）, 4: 赠品, 5: 兑换券（已不用）
  // 倒计时相关属性
  giftCountDown?: number; // 赠品倒计时时间
  giftAllowType?: number; // 赠品是否可结算 1可结算 giftCountDown为可计算结束时间倒计时  2不可结算 giftCountDown为可计算开始时间倒计时
  itemGiftInfo?: any; // 独立赠品的intro提示
  // Swiper管理器
  swiperManager?: any;
}

export function CartItem(props: CartItemProps) {
  // 获取设备类型
  const { deviceType } = useContext(ResponsiveContext);

  // 使用自定义hooks管理业务逻辑
  const cartItemHooks = useCartItem(props);
  return (
    <div className="cart-item">
      {/* PC端版本 */}
      <WebOnly>
        <CartItemPC {...props} {...cartItemHooks} />
      </WebOnly>

      {/* 移动端版本 */}
      <WapOnly>
        <CartItemMobile {...props} {...cartItemHooks} swiperManager={props.swiperManager} />
      </WapOnly>

      {/* 颜色更换弹窗 */}
      {cartItemHooks.canChangeColorClick() && (
        <ColorChangePopup
          cartItem={props.cartItemData}
          open={cartItemHooks.showChangeColor}
          anchorEl={cartItemHooks.colorPopoverAnchor}
          onClose={cartItemHooks.handleColorPopupClose}
          loading={cartItemHooks.loading}
          onReload={props.onReload}
        />
      )}

      {/* 购物车详情弹窗 - 仅在移动端显示 */}
      {deviceType !== EPlatform.WEB && (
        <CartDetailPopup
          open={cartItemHooks.showCartDetailPopup}
          onClose={cartItemHooks.handleCartDetailPopupClose}
          cartItemData={props.cartItemData}
          selected={props.selected}
          itemPromotion={props.itemPromotion}
          itemType={props.itemType}
          currency={props.currency}
          handleEditPrescription={cartItemHooks.handleEditPrescription}
          handleRemoveLens={cartItemHooks.handleRemoveLens}
        />
      )}

      {/* 购物车项目确认对话框 */}
      <CartItemDialog
        open={cartItemHooks.showCartItemDialog}
        onClose={() => cartItemHooks.setShowCartItemDialog(false)}
        content={cartItemHooks.cartItemDialogProps.content}
        content2={cartItemHooks.cartItemDialogProps.content2}
        content2TextAlignCenter={cartItemHooks.cartItemDialogProps.content2TextAlignCenter}
        confirmText={cartItemHooks.cartItemDialogProps.confirmText}
        cancelText={cartItemHooks.cartItemDialogProps.cancelText}
        removeLens={cartItemHooks.cartItemDialogProps.removeLens}
        onConfirm={cartItemHooks.cartItemDialogProps.onConfirm}
        onRemoveLens={cartItemHooks.cartItemDialogProps.onRemoveLens}
      />

      {/* 限购信息弹窗 */}
      <LimitBuyDialog
        open={cartItemHooks.showLimitBuyDialog}
        onClose={() => cartItemHooks.setShowLimitBuyDialog(false)}
        cartItemData={props.cartItemData}
        itemPromotion={props.itemPromotion}
      />

      {/* 兑换确认弹窗 */}
      <RedeemedConfirmDialog
        open={cartItemHooks.showRedeemedConfirmDialog}
        onClose={cartItemHooks.handleRedeemedCancel}
        exchangeCouponList={cartItemHooks.exchangeCouponList}
        onConfirm={cartItemHooks.handleRedeemedConfirm}
      />

      {/* 配镜弹窗 */}
      <LensProcessDialog
        open={cartItemHooks.showLensProcessDialog}
        onClose={cartItemHooks.handleLensProcessDialogClose}
        cartItemData={cartItemHooks.lensProcessCartItemData as any}
        reloadCart={props.onReload}
      />
    </div>
  );
}
